import { STDocument } from '@st/sdk'
import { JSONValue } from '@st/util/json-value'
import { asc } from '@st/util/sort'

export type DocumentWarningContext = {
  type: string
  year: number
}

export type DocumentWarning =
  | {
      type: 'duplicate'
      originalDocumentId: string
      originalDocumentName: string
    }
  | { type: 'password_protected' }
  | { type: 'tax_year_mismatch'; actualYear: number; expectedYear: number }

export function formatDocumentWarning(warning: DocumentWarning) {
  switch (warning.type) {
    case 'duplicate':
      return `⚠️ Duplicate of ${warning.originalDocumentName}`
    case 'password_protected':
      return '⚠️ Document is password protected'
    case 'tax_year_mismatch':
      return `⚠️ Tax year mismatch. Expected ${warning.expectedYear}, but got ${warning.actualYear}`
  }
}

export function selWarningsByDocumentId(
  documents: STDocument[],
  context: DocumentWarningContext
): Record<string, DocumentWarning[]> {
  // if a document does not have a has, we do not consider it
  // because anything will come back inconclusive
  const canidateDocuments = documents.filter((doc) => doc.hash != undefined).sort(asc('uploadedAt'))

  const originalDocumentsByHash: Record<string, STDocument> = {}
  for (const doc of canidateDocuments) {
    if (originalDocumentsByHash[doc.hash!]) {
      continue
    }
    originalDocumentsByHash[doc.hash!] = doc
  }

  const documentWarnings: Record<string, DocumentWarning[]> = {}

  for (const doc of canidateDocuments) {
    if (doc.passwordProtected) {
      documentWarnings[doc.id] ||= []
      documentWarnings[doc.id].push({ type: 'password_protected' })
    }
    // duplicate document
    if (originalDocumentsByHash[doc.hash!] && originalDocumentsByHash[doc.hash!].id != doc.id) {
      documentWarnings[doc.id] ||= []
      documentWarnings[doc.id].push({
        type: 'duplicate',
        originalDocumentId: originalDocumentsByHash[doc.hash!].id,
        originalDocumentName: originalDocumentsByHash[doc.hash!].name
      })
    }

    const documentTaxYear = doc.fields.find((f) => f.key == 'tax_year')?.value
    if (isValidNumber(documentTaxYear) && documentTaxYear != context.year.toString()) {
      documentWarnings[doc.id] ||= []
      documentWarnings[doc.id].push({
        type: 'tax_year_mismatch',
        actualYear: Number(documentTaxYear),
        expectedYear: context.year
      })
    }
  }

  return documentWarnings
}

function isValidNumber(str: JSONValue) {
  return typeof str === 'string' && str && Number.isFinite(Number(str))
}
